import { type INoteAttachment, NoteDataType } from '@/service/note';

export function hasOnlyUrlProperty(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 1 && Object.prototype.hasOwnProperty.call(obj, 'url');
}

const FileType = [
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/octet-stream',
  'text/markdown',
  'text/x-markdown',
  'audio/wav',
  'audio/vnd.wave',
  'audio/wave',
  'audio/mpeg',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
  'audio/aac',
  'video/mp4'
];

const FileTypeDescription = {
  'application/msword': 'Word',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPT',
  'text/plain': 'Text',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/pdf': 'PDF',
  'text/csv': 'CSV',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/octet-stream': 'File',
  'text/markdown': 'Markdown',
  'text/x-markdown': 'Markdown',
  'audio/wav': 'Voice Note',
  'audio/vnd.wave': 'Voice Note',
  'audio/wave': 'Voice Note',
  'audio/mpeg': 'Voice Note',
  'audio/m4a': 'Voice Note',
  'audio/x-m4a': 'Voice Note',
  'audio/mp4': 'Voice Note',
  'audio/aac': 'Voice Note',
  'video/mp4': 'Voice Note'
};

export function isFileAttachment(obj: Record<string, any>): boolean {
  return obj.metadata?.ogi?.type && FileType.includes(obj.metadata?.ogi?.type);
}

export function isImageAttachment(obj: INoteAttachment = {}): boolean {
  return obj.metadata?.ogi?.type?.startsWith('image/') ?? false;
}

export function isPDFFileAttachment(obj: Record<string, any>): boolean {
  return obj.metadata?.ogi?.type === 'application/pdf';
}

export function isVoiceFileAttachment(obj: INoteAttachment = {}): boolean {
  return (
    [
      'audio/wav',
      'audio/vnd.wave',
      'audio/wave',
      'audio/x-wav',
      'audio/mpeg',
      'video/mp4',
      'audio/mp4',
      'audio/x-m4a',
      'audio/aac'
    ].includes(obj.metadata?.ogi?.type || '') ||
    isVoiceAttachment(obj) ||
    isShortVoiceAttachment(obj)
  );
}

export function isVoiceAttachment(obj: INoteAttachment = {}): boolean {
  return obj.dataType === NoteDataType.AUDIO;
}

export function isShortVoiceAttachment(obj: INoteAttachment = {}): boolean {
  return obj.dataType === NoteDataType.SHORT_AUDIO;
}

export function getDefaultDescription(obj: INoteAttachment): string {
  // @ts-expect-error any
  return obj.metadata?.ogi?.type ? FileTypeDescription[obj.metadata.ogi.type] || 'File' : 'File';
}

// 附件分类，图片附件/其他附件
export function attachmentClassify(
  attachments: INoteAttachment[]
): [INoteAttachment[], INoteAttachment[], string[], INoteAttachment | null] {
  const otherAttachments: INoteAttachment[] = [];
  const pictureAttachments: INoteAttachment[] = [];
  const pictureUrlInAtt: string[] = [];
  let firstFile: any = null;

  try {
    attachments.forEach((item) => {
      if (isImageAttachment(item)) {
        pictureAttachments.push(item);
      } else {
        if (item.metadata?.ogi?.icon !== item.metadata?.ogi?.image && !!item.metadata?.ogi?.image) {
          pictureUrlInAtt.push(item.metadata.ogi.image);
        }

        // 兼容老逻辑，老逻辑里没有metadata，会出现样式错误
        if (Object.prototype.hasOwnProperty.call(item, 'metadata')) {
          otherAttachments.push(item);
        }
      }

      if (isFileAttachment(item) && !firstFile) {
        firstFile = item;
      }
    });
  } catch (e) {
    console.log('Attachments error', attachments);
  }

  return [otherAttachments, pictureAttachments, pictureUrlInAtt, firstFile];
}

export function isDiffAttachments(att1: INoteAttachment[], att2: INoteAttachment[]): boolean {
  if (att1.length !== att2.length) {
    return true;
  }

  for (let i = 0; i < att1.length; i++) {
    if (att1[i].url !== att2[i].url) {
      return true;
    }
  }

  return false;
}
